import { useMemo } from "react";

/**
 * Hook para calcular pantallas operativas por mes y dividir valores del ER por unidad.
 *
 * Fuentes de pantallas:
 * 1. Edificios: pantallas + totems + espera + cowork
 *    (estado OPERATIVO, inicio_servicio <= inicio del mes)
 * 2. Cartelería/Gestión: cantidad_pantallas
 *    (estado Activo, fecha_inicio <= inicio del mes, no eliminada)
 */
export const useUnitEconomics = ({ edificios, negociosCarteleria, selectedYear }) => {
  // Pantallas operativas al inicio de cada mes (array de 12)
  const pantallasOperativasMensual = useMemo(() => {
    const edificiosArr = Object.values(edificios || {});
    const carteleria = (negociosCarteleria || []).filter(
      (n) => n.estado === "Activo" && !n.eliminada && n.cantidad_pantallas
    );

    return Array.from({ length: 12 }, (_, mesIndex) => {
      const inicioMes = new Date(selectedYear, mesIndex, 1);

      const pantallasEdificios = edificiosArr.reduce((sum, e) => {
        if (e.estado !== "OPERATIVO" || !e.inicio_servicio) return sum;
        const inicio = new Date(e.inicio_servicio);
        if (inicio > inicioMes) return sum;
        return sum + (e.pantallas || 0) + (e.totems || 0) + (e.espera || 0) + (e.cowork || 0);
      }, 0);

      const pantallasCarteleria = carteleria.reduce((sum, n) => {
        if (!n.fecha_inicio) return sum;
        const inicio = new Date(n.fecha_inicio);
        if (inicio > inicioMes) return sum;
        return sum + (n.cantidad_pantallas || 0);
      }, 0);

      return pantallasEdificios + pantallasCarteleria;
    });
  }, [edificios, negociosCarteleria, selectedYear]);

  // ¿Están completos los datos de inicio_servicio?
  const datosCompletos = useMemo(() => {
    const edificiosArr = Object.values(edificios || {});
    const operativos = edificiosArr.filter((e) => e.estado === "OPERATIVO");
    if (operativos.length === 0) return false;
    return operativos.every((e) => !!e.inicio_servicio);
  }, [edificios]);

  // Dividir un array mensual por pantallas operativas
  const dividirPorPantallas = useMemo(
    () => (arrayMensual) => {
      if (!arrayMensual) return Array(12).fill(0);
      return arrayMensual.map((val, i) => {
        const p = pantallasOperativasMensual[i];
        if (!p || p === 0) return 0;
        return (val || 0) / p;
      });
    },
    [pantallasOperativasMensual]
  );

  return {
    pantallasOperativasMensual,
    datosCompletos,
    dividirPorPantallas,
  };
};
